/**
 * API-Client fuer die Split-/Merge-Historie einer Bestellung - `GET /api/orders/:id/lineage`
 * (Task 7, `backend/src/http/routes/orderLineage.ts`). Frontend-seitige Kopie der Backend-Typform
 * (`backend/src/domaene/splitEvents.ts`), camelCase wie dort - dieselbe Trennung wie bei
 * `JtlBestellungRoh` in `jtlBestellungen.ts` (Frontend und Backend teilen hier keinen Code).
 *
 * Aufrufer: Task 11 (Historie-Komponente im Drawer).
 */
import { anfrage } from "./basis.ts";

export interface LineagePosition {
  posId: number;
  sku: string | null;
  mengeVorher: number;
  mengeVerschoben: number;
  mengeNachher: number;
}

export interface LineageQuelle {
  orderId: string;
  positionen: LineagePosition[];
}

export interface LineageOptionen {
  dokumente: boolean;
  tasks: boolean;
  /** Nummer der Teillieferung, vermerkt bei der Rueckfuehrung (das Ziel ist danach geloescht, `targetOrderId` null). */
  zielNummer?: string;
}

export interface LineageEreignis {
  id: string;
  eventType: "split" | "merge";
  targetOrderId: string | null;
  quellen: LineageQuelle[];
  optionen: LineageOptionen | null;
  createdAt: string;
  createdBy: string | null;
}

export interface LineageRueckfuehrungsPlanZeile {
  art: "aendern" | "neu";
  kLieferantenBestellungPos: number | null;
  kArtikel: number;
  artikelNummer: string | null;
  mengeVorher: number;
  mengeZurueck: number;
  mengeNachher: number;
  ekNetto: number;
  mwst: number;
  lieferdatum: string | null;
}

/** `options` eines split_undo-Ereignisses (Backend: `RueckfuehrungsOptionen`, `splitEvents.ts`). */
export interface LineageRueckfuehrungsOptionen {
  stand: "geplant" | "aufgefuellt" | "abgeschlossen";
  plan: LineageRueckfuehrungsPlanZeile[];
  dokumentIds: string[];
  taskIds: string[];
  uebernommeneDokumentIds: string[];
  kindNummer: string;
  quelleNummer: string;
}

/**
 * Rueckfuehrung einer Teillieferung (split_undo). `quellen` traegt orderId = Kind, aber die Mengen
 * der Quelle - NICHT mit der Split-Mengenformel (vorher - nachher = verschoben) darstellen.
 */
export interface LineageRueckfuehrungsEreignis {
  id: string;
  eventType: "split_undo";
  targetOrderId: string | null;
  quellen: LineageQuelle[];
  optionen: LineageRueckfuehrungsOptionen;
  createdAt: string;
  createdBy: string | null;
}

export interface Lineage {
  /** Das Event, aus dem DIESE Bestellung entstanden ist - null, wenn sie kein Split-/Merge-Ziel ist. */
  asTarget: LineageEreignis | null;
  /** Alle Events, bei denen DIESE Bestellung als Quelle beteiligt war (koennen mehrere sein). */
  asSource: (LineageEreignis | LineageRueckfuehrungsEreignis)[];
  /** split_undo-Ereignisse, deren Ziel DIESE Bestellung ist (in sie zurueckgefuehrte Kinder). Fehlt bei aelteren Antworten. */
  zurueckgefuehrt?: LineageRueckfuehrungsEreignis[];
}

/**
 * Laedt die Lineage einer Bestellung. `orderId` geht als Kennung der Route durch - auch eine
 * abgeleitete JTL-Kennung (`jtl-<n>`) ist zulaessig, die Route loest sie serverseitig auf
 * (`loeseKennungZumLesenAuf`) und liefert bei einer unbeteiligten Bestellung die leere Form
 * (`{ asTarget: null, asSource: [] }`) statt eines 404.
 */
export async function lineageLaden(orderId: string, signal?: AbortSignal): Promise<Lineage> {
  return await anfrage<Lineage>(`/api/orders/${encodeURIComponent(orderId)}/lineage`, { signal });
}
